/**
 * NaraSpeller (Pusan National University) Provider for grammar checking
 */

import { BaseProvider } from './BaseProvider'
import type { CheckOptions, ProviderType, ProviderConfig } from './types'
import type { GrammarError } from '$lib/types/grammar'
import {
  parseNaraSpellerResponse,
  mapCorrectMethodToLevel,
  mapCorrectMethodToType,
  getErrorDescription,
  type NaraSpellerError
} from '$lib/utils/xmlParser'

export class NaraSpellerProvider extends BaseProvider {
  private weakOpt: 0 | 1 = 0
  private useProxy: boolean = true  // Always use proxy due to CORS

  constructor(config?: ProviderConfig) {
    super(config)
    // Use proxy server to avoid CORS issues
    const proxyUrl = import.meta.env.VITE_PROXY_URL || 'https://appstore.junlim.org'
    this.serverUrl = `${proxyUrl}/api/nara-speller`
    this.weakOpt = config?.options?.weakOpt ?? 0
  }

  getName(): string {
    return '부산대 맞춤법 검사기'
  }

  getDescription(): string {
    return '부산대학교에서 제공하는 한국어 맞춤법 검사 서비스입니다. 프록시 서버를 통해 연결됩니다.'
  }

  requiresApiKey(): boolean {
    return true
  }

  requiresServerUrl(): boolean {
    return false // URL is fixed
  }

  getType(): ProviderType {
    return 'naraSpeller'
  }

  async checkText(text: string, options?: CheckOptions): Promise<GrammarError[]> {
    if (!text || text.trim().length === 0) {
      return []
    }

    if (!this.apiKey) {
      throw new Error('NaraSpeller API 키가 필요합니다')
    }

    // NaraSpeller has a text length limit (assumed to be around 5000 characters)
    const MAX_TEXT_LENGTH = 5000
    if (text.length > MAX_TEXT_LENGTH) {
      throw new Error(`텍스트가 너무 깁니다. ${MAX_TEXT_LENGTH}자 이하로 입력해주세요.`)
    }

    try {
      // Use weakOpt from options if provided, otherwise use instance setting
      const weakOptValue = options?.weakOpt ?? this.weakOpt
      
      // Call through proxy server
      const response = await this.makeRequest(this.serverUrl || '', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text,
          apiKey: this.apiKey,
          weakOpt: weakOptValue
        })
      })

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('API 키가 유효하지 않습니다')
        }
        if (response.status === 429) {
          throw new Error('일일 사용 한도를 초과했습니다')
        }
        if (response.status === 501) {
          throw new Error('프록시 서버가 NaraSpeller를 아직 지원하지 않습니다')
        }
        throw new Error(`서버 오류: ${response.status} ${response.statusText}`)
      }

      // Get response from proxy (already converted to JSON)
      const data = await response.json()
      
      // Check if response contains errors array
      if (data.errors && Array.isArray(data.errors)) {
        // If proxy already parsed the XML, use the parsed errors
        return this.mapProxyErrors(data.errors, text)
      } else if (data.xml) {
        // If proxy returns raw XML, parse it
        const errors = parseNaraSpellerResponse(data.xml)
        return this.mapToGrammarErrors(errors, text)
      } else {
        // No errors found
        return []
      }
    } catch (error) {
      console.error('NaraSpeller provider error:', error)
      
      // Provide more user-friendly error messages
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          throw new Error('네트워크 연결 오류가 발생했습니다. 인터넷 연결을 확인해주세요.')
        }
        if (error.message.includes('XML')) {
          throw new Error('서버 응답 형식이 올바르지 않습니다.')
        }
        throw error
      }
      
      throw new Error('NaraSpeller API 연결 실패')
    }
  }

  /**
   * Decode HTML entities in text
   */
  private decodeHtmlEntities(text: string): string {
    return text
      .replace(/&apos;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/<br\s*\/?>/gi, '\n');
  }

  /**
   * Map proxy errors to GrammarError format
   */
  private mapProxyErrors(proxyErrors: any[], originalText: string): GrammarError[] {
    return proxyErrors.map((error, index) => ({
      id: this.generateId(),
      type: error.type || 'spelling',
      level: error.level || 'error',
      original: error.originalText || error.original || '',
      originalText: error.originalText || error.original || '',
      position: {
        start: error.position?.start || error.start || 0,
        end: error.position?.end || error.end || 0
      },
      corrections: Array.isArray(error.corrections) 
        ? error.corrections.map((c: any) => ({
            id: this.generateId(),
            text: this.decodeHtmlEntities(typeof c === 'string' ? c : c.text)
          }))
        : [],
      message: this.decodeHtmlEntities(error.description || error.message || '맞춤법 오류'),
      description: this.decodeHtmlEntities(error.description || error.message || '맞춤법 오류'),
      help: error.help ? this.decodeHtmlEntities(error.help) : undefined
    }))
  }

  /**
   * Map NaraSpeller errors to GrammarError format
   */
  private mapToGrammarErrors(naraErrors: NaraSpellerError[], originalText: string): GrammarError[] {
    const positionMap = new Map<string, GrammarError>()
    
    // Track cumulative offset for position adjustments
    let cumulativeOffset = 0
    
    for (let i = 0; i < naraErrors.length; i++) {
      const error = naraErrors[i]
      
      // Calculate actual position in the original text
      // NaraSpeller provides positions relative to the processed segment
      const actualStart = error.start + cumulativeOffset
      const actualEnd = error.end + cumulativeOffset
      
      // Validate position bounds
      if (actualStart < 0 || actualEnd > originalText.length) {
        console.warn('Invalid error position, skipping:', error)
        continue
      }
      
      const posKey = `${actualStart}-${actualEnd}`
      
      // Skip if we already have an error at this position
      if (positionMap.has(posKey)) {
        continue
      }
      
      const grammarError: GrammarError = {
        id: this.generateId(),
        type: mapCorrectMethodToType(error.correctMethod),
        level: mapCorrectMethodToLevel(error.correctMethod),
        original: error.orgStr || originalText.substring(actualStart, actualEnd),
        originalText: error.orgStr || originalText.substring(actualStart, actualEnd),
        position: {
          start: actualStart,
          end: actualEnd
        },
        corrections: this.mapCorrections(error),
        message: getErrorDescription(error.correctMethod, error.help),
        description: getErrorDescription(error.correctMethod, error.help)
      }
      
      // Add additional metadata if available
      if (error.help && error.help.trim()) {
        grammarError.help = error.help
      }
      
      // Add to position map (no duplicates)
      positionMap.set(posKey, grammarError)
      
      // Update cumulative offset if the correction changes text length
      const originalLength = error.orgStr?.length || (actualEnd - actualStart)
      const correctionLength = error.candWord?.length || originalLength
      const lengthDiff = correctionLength - originalLength
      
      // Note: This offset tracking may need adjustment based on actual NaraSpeller behavior
      // cumulativeOffset += lengthDiff
    }
    
    // Return unique errors sorted by position
    return Array.from(positionMap.values()).sort((a, b) => {
      const aStart = a.position?.start || 0
      const bStart = b.position?.start || 0
      return aStart - bStart
    })
  }

  /**
   * Map NaraSpeller corrections to our format
   */
  private mapCorrections(error: NaraSpellerError): Array<{ id: string; text: string }> {
    const corrections: Array<{ id: string; text: string }> = []
    
    if (error.candWord && error.candWord.trim()) {
      // NaraSpeller typically provides a single correction
      // But it might be comma-separated for multiple suggestions
      const suggestions = error.candWord.split(',').map(s => s.trim()).filter(s => s)
      
      for (const suggestion of suggestions) {
        corrections.push({
          id: this.generateId(),
          text: suggestion
        })
      }
    }
    
    // If no corrections provided, return empty array
    return corrections
  }

  /**
   * Test connection to NaraSpeller API
   */
  async testConnection(): Promise<{ success: boolean; message: string; responseTime?: number }> {
    if (!this.apiKey) {
      return {
        success: false,
        message: 'API 키가 설정되지 않았습니다'
      }
    }

    const startTime = Date.now()
    
    try {
      // Test with a simple text
      const testText = '안녕하세요'
      const errors = await this.checkText(testText)
      
      return {
        success: true,
        message: '연결 성공',
        responseTime: Date.now() - startTime
      }
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : '연결 실패',
        responseTime: Date.now() - startTime
      }
    }
  }

  /**
   * Validate configuration with NaraSpeller specific rules
   */
  validateConfig(config: ProviderConfig): { valid: boolean; errors: string[] } {
    const validation = super.validateConfig(config)
    
    // Additional NaraSpeller specific validation
    if (config.apiKey && config.apiKey.length < 20) {
      validation.errors.push('API 키가 너무 짧습니다')
      validation.valid = false
    }
    
    if (config.options?.weakOpt !== undefined) {
      if (config.options.weakOpt !== 0 && config.options.weakOpt !== 1) {
        validation.errors.push('검사 강도는 0(강함) 또는 1(약함)이어야 합니다')
        validation.valid = false
      }
    }
    
    return validation
  }

  /**
   * Initialize with NaraSpeller specific options
   */
  initialize(config: ProviderConfig): void {
    super.initialize(config)
    
    // Set NaraSpeller specific options
    if (config.options?.weakOpt !== undefined) {
      this.weakOpt = config.options.weakOpt
    }
    
    // Always use the proxy URL
    const proxyUrl = import.meta.env.VITE_PROXY_URL || 'https://appstore.junlim.org'
    this.serverUrl = `${proxyUrl}/api/nara-speller`
  }
}